/**
 * Where you are signed in, and how to stop being (`docs/AuthSessions.md` §7).
 *
 * Lives on the Security page rather than getting its own nav entry: it is the natural
 * neighbour of Change password, and the place someone looks after a password reset — which
 * now revokes every other session — to confirm it took effect.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, MonitorSmartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/hooks/useConfirm';
import { useRevokeSession, useSessions, type Session } from '@/hooks/useSessions';

/**
 * A label a person can recognise, from what the UA will admit to.
 *
 * Mirrors `describeBrowser` in `usePush.ts`, which builds the same label client-side at
 * enrollment time. Duplicated rather than shared because that one names *this* browser
 * from `navigator`, while this one names *another* browser from a stored string — the
 * inputs differ even though the output reads the same.
 */
function describeSession(session: Session): string {
  const ua = session.userAgent;
  if (!ua) return 'Unknown device';

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Unknown browser';

  const platform = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(ua)
      ? 'iOS'
      : /Macintosh/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;

  return platform ? `${browser} on ${platform}` : browser;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function SignedInDevices() {
  const navigate = useNavigate();
  const { data: sessions, isLoading, isError } = useSessions();
  const revoke = useRevokeSession();
  const { confirm, confirmDialog } = useConfirm();

  // Current session first, then most recently used. Someone scanning for "what is that
  // other device" should not have to find their own row among them.
  const ordered = useMemo(
    () =>
      [...(sessions ?? [])].sort((a, b) => {
        if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
        return b.lastSeenAt.localeCompare(a.lastSeenAt);
      }),
    [sessions],
  );

  const signOut = async (session: Session) => {
    const ok = await confirm({
      title: 'Sign out this device?',
      message: session.isCurrent
        ? 'This is the device you are using now. You will be signed out immediately and will need to sign in again.'
        : `${describeSession(session)} will be signed out immediately.`,
      isDanger: true,
      confirmText: 'Sign out',
    });
    if (!ok) return;

    revoke.mutate(session.id, {
      onSuccess: () => {
        // Revoking your own session leaves this page holding a cookie the server will now
        // refuse — and `DELETE /sessions/{id}` cannot clear it, because the cookie is
        // HttpOnly and only a `Set-Cookie` can. So hand off to `/signout`, which does the
        // one thing that ends a session properly. Its logout call finding the row already
        // revoked is fine: it clears the cookie regardless.
        if (session.isCurrent) void navigate('/signout');
      },
    });
  };

  return (
    <section className="bg-card border border-border rounded-lg">
      <header className="px-5 py-4 border-b border-border">
        <h2 className="text-sm font-semibold">Signed-in devices</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Sign out anything you don&rsquo;t recognise. Changing your password signs out every device.
        </p>
      </header>

      <div className="p-5">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : isError ? (
          <p className="text-xs text-muted-foreground">Couldn&rsquo;t load your signed-in devices.</p>
        ) : ordered.length === 0 ? (
          // Reachable only for a legacy cookie, which names no session row.
          <p className="text-xs text-muted-foreground">No other devices are signed in.</p>
        ) : (
          <ul className="divide-y divide-border">
            {ordered.map((session) => (
              <li key={session.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <MonitorSmartphone className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm truncate">{describeSession(session)}</span>
                    {session.isCurrent && (
                      <Badge variant="secondary" className="text-[10px]">
                        This device
                      </Badge>
                    )}
                    {session.extended && (
                      <Badge variant="outline" className="text-[10px]">
                        Installed app
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    Last used {formatWhen(session.lastSeenAt)}
                    {session.sourceIp ? ` · ${session.sourceIp}` : ''}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={revoke.isPending}
                  onClick={() => void signOut(session)}
                >
                  Sign out
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDialog}
    </section>
  );
}
